import type { CellProperties, BorderSide } from '../model/form.types';
import { TABLE_BORDER_DEFAULTS } from './table-constants';

export type BorderPattern =
  | 'all'
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top-bottom'
  | 'left-right'
  | 'none';

export type BorderStyleType = 'none' | 'solid' | 'dashed' | 'dotted';

export interface BorderProperties {
  color: string;
  width: number;
  style: BorderStyleType;
}

function hasBorderSide(side?: BorderSide): boolean {
  return !!(side && (side.width ?? 1) > 0 && side.style !== 'none');
}

export function detectBorderPattern(cellProperties?: CellProperties): BorderPattern {
  const border = cellProperties?.border;
  if (!border) return 'none';

  const hasTop = hasBorderSide(border.top);
  const hasRight = hasBorderSide(border.right);
  const hasBottom = hasBorderSide(border.bottom);
  const hasLeft = hasBorderSide(border.left);

  const count = [hasTop, hasRight, hasBottom, hasLeft].filter(Boolean).length;

  if (count === 0) return 'none';
  if (count === 4) return 'all';

  // Map border combinations to patterns
  const signature = `${hasTop}-${hasRight}-${hasBottom}-${hasLeft}`;
  const patternMap: Record<string, BorderPattern> = {
    'true-false-false-false': 'top',
    'false-true-false-false': 'right',
    'false-false-true-false': 'bottom',
    'false-false-false-true': 'left',
    'true-false-true-false': 'top-bottom',
    'false-true-false-true': 'left-right',
  };

  return patternMap[signature] || 'all';
}

export function resolveBorderPattern(cellProperties?: CellProperties): BorderPattern {
  if (!cellProperties?.border) return TABLE_BORDER_DEFAULTS.PATTERN;
  return detectBorderPattern(cellProperties);
}

export function extractBorderProperties(cellProperties?: CellProperties): BorderProperties {
  const border = cellProperties?.border;
  if (!border) {
    return {
      color: TABLE_BORDER_DEFAULTS.COLOR,
      width: TABLE_BORDER_DEFAULTS.WIDTH,
      style: TABLE_BORDER_DEFAULTS.STYLE,
    };
  }

  const colors: string[] = [];
  const widths: number[] = [];
  const styles: BorderStyleType[] = [];

  (['top', 'right', 'bottom', 'left'] as const).forEach((side) => {
    const sideBorder = border[side];
    if (sideBorder) {
      if (sideBorder.color) {
        colors.push(sideBorder.color);
      }
      if (sideBorder.width !== undefined && sideBorder.width > 0) {
        widths.push(sideBorder.width);
      }
      if (sideBorder.style && sideBorder.style !== 'none') {
        styles.push(sideBorder.style);
      }
    }
  });

  function getMostCommon<T>(arr: T[]): T | undefined {
    if (arr.length === 0) return undefined;
    const counts = new Map<T, number>();
    arr.forEach((item) => {
      counts.set(item, (counts.get(item) || 0) + 1);
    });
    let maxCount = 0;
    let mostCommon: T | undefined;
    counts.forEach((count, item) => {
      if (count > maxCount) {
        maxCount = count;
        mostCommon = item;
      }
    });
    return mostCommon;
  }

  return {
    color: getMostCommon(colors) || colors[0] || TABLE_BORDER_DEFAULTS.COLOR,
    width: getMostCommon(widths) || widths[0] || TABLE_BORDER_DEFAULTS.WIDTH,
    style: getMostCommon(styles) || styles[0] || TABLE_BORDER_DEFAULTS.STYLE,
  };
}

export const BORDER_PATTERNS: Array<{ value: BorderPattern; label: string }> = [
  { value: 'all', label: 'Todas as bordas' },
  { value: 'top', label: 'Borda superior' },
  { value: 'right', label: 'Borda direita' },
  { value: 'bottom', label: 'Borda inferior' },
  { value: 'left', label: 'Borda esquerda' },
  { value: 'top-bottom', label: 'Eixo Y (superior e inferior)' },
  { value: 'left-right', label: 'Eixo X (esquerda e direita)' },
  { value: 'none', label: 'Sem bordas' },
];
